import { useEffect, useRef, useState } from "react";
import { masryClient } from "../apiClient/masryClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import type { MasryAnswerUsage } from "../types/masry.types";

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  sources?: string[];
  usage?: MasryAnswerUsage;
}

/** The conversation itself — messages, input, send logic. Used inside the
 *  floating Masry widget panel. */
export function MasryChatPanel() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  async function handleSend() {
    const question = input.trim();
    if (!question || sending) return;

    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setSending(true);
    setError(null);

    try {
      const result = await masryClient.ask(question);
      setMessages((m) => [...m, { role: "assistant", text: result.answer, sources: result.sources, usage: result.usage }]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Masry couldn't answer that — try again.");
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  return (
    <>
      <div className="masry-messages">
        {messages.length === 0 ? (
          <div className="masry-empty">
            <p>Ask a question to get started — e.g. &ldquo;How many annual leave days do I get?&rdquo;</p>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`masry-message masry-message--${m.role}`}>
              <div className="masry-bubble">
                <p>{m.text}</p>
                {m.sources && m.sources.length > 0 && <p className="masry-sources">Sources: {m.sources.join(", ")}</p>}
                {m.usage && (
                  <p className="masry-sources">
                    {m.usage.embeddingTokens + m.usage.promptTokens} in · {m.usage.completionTokens} out tokens
                  </p>
                )}
              </div>
            </div>
          ))
        )}
        {sending && (
          <div className="masry-message masry-message--assistant">
            <div className="masry-bubble masry-bubble--pending">Masry is thinking…</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && <div className="form-banner form-banner--error masry-error">{error}</div>}

      <div className="masry-input-row">
        <textarea
          className="masry-input"
          rows={2}
          placeholder="Ask Masry a question about company policy…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <button type="button" className="btn btn-primary" onClick={handleSend} disabled={sending || !input.trim()}>
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </>
  );
}
